import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import {
  PiBriefcaseLight,
  PiCoinsLight,
  PiClockLight,
  PiGaugeLight,
  PiHourglassLight,
} from 'react-icons/pi';
import { useAppDispatch } from '../store';
import { isMatterEconomicsEnabled } from '../utils/config';
import { fetchFirmEconomics } from '../store/actions/economicsActions';
import { formatCurrency } from '../utils/formatCurrency';
import Header from '../components/shared/TailwindComponents/Header';
import Skeleton from '../components/shared/TailwindComponents/Skeleton';
import Spinner from '../components/shared/TailwindComponents/Spinner';
import Button from '../components/shared/TailwindComponents/Button';
import GlassCard from '../components/shared/brand/GlassCard';
import { KpiTile } from '../components/shared/brand/Field';
import FirmEconomicsFilterBar from '../components/Economics/FirmEconomicsFilterBar';
import {
  TopMattersWidget,
  TopClientsWidget,
  HoursByUserWidget,
  RevenueTrendWidget,
} from '../components/Economics/firmEconomicsWidgets';
import type { FirmEconomics, FirmEconomicsFilters } from '../types/economics';

/**
 * Firm economics dashboard (US #2239). Filter bar (#2279) drives four widgets
 * (#2278); matter rows drill into the per-matter Economics tab and user rows
 * filter the dashboard (#2280). Route + sidebar item are flag-gated (#2281).
 *
 * A dashboard, deliberately NOT bound to DetailPageSurface: there is no record
 * here to dock context panes around — it is a filter bar over a KPI strip and
 * four glass widgets.
 */
const FirmEconomicsDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [filters, setFilters] = useState<FirmEconomicsFilters>({});
  const [data, setData] = useState<FirmEconomics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    setForbidden(false);
    try {
      const res = await dispatch(fetchFirmEconomics(filters));
      setData(res || null);
    } catch (e: any) {
      if (e?.response?.status === 403) setForbidden(true);
      else setError(true);
    } finally {
      setLoading(false);
    }
  }, [dispatch, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = data?.totals || {};
  const currency = filters.currency || totals.by_currency?.[0]?.currency || 'eur';
  const money = (v?: number | null) => formatCurrency(Number(v || 0), currency);
  const byCurrency = totals.by_currency || [];
  const isMixed = totals.revenue == null && byCurrency.length > 1;
  // Single-currency total, or "Mixed currencies" (each currency then gets its
  // own tile in the by-currency strip below) when the BE returns a null total.
  let revenueValue: React.ReactNode;
  if (totals.revenue != null) {
    revenueValue = money(totals.revenue);
  } else if (isMixed) {
    revenueValue = (
      <span title={byCurrency.map((c) => formatCurrency(c.revenue, c.currency)).join(' · ')}>
        {t('Mixed currencies')}
      </span>
    );
  } else if (byCurrency.length === 1) {
    revenueValue = formatCurrency(byCurrency[0].revenue, byCurrency[0].currency);
  } else {
    revenueValue = money(0);
  }

  const totalHours = Number(totals.total_hours || 0);
  const billableHours = Number(totals.billable_hours || 0);
  // Firm-wide utilization: the share of recorded time that is billable. Only
  // meaningful once there is time to divide by.
  const utilization = totalHours > 0 ? Math.round((billableHours / totalHours) * 100) : null;

  const onUserClick = (userId: number) => setFilters((p) => ({ ...p, user_id: [userId] }));

  const widgets = useMemo(() => ({
    matters: data?.top_matters || [],
    clients: data?.per_client || [],
    users: data?.per_user || [],
    trend: data?.revenue_trend || [],
  }), [data]);

  const renderMessage = () => {
    if (forbidden) {
      return (
        <div className="text-sm text-fg-muted py-10 text-center">
          {t('You do not have access to firm economics.')}
        </div>
      );
    }
    if (error) {
      return (
        <div className="py-10 text-center space-y-3">
          <p className="text-sm text-fg-muted">{t('Could not load firm economics.')}</p>
          <div className="flex justify-center">
            <Button variant="outline" size="small" onClick={load}>{t('Retry')}</Button>
          </div>
        </div>
      );
    }
    return null;
  };

  if (!isMatterEconomicsEnabled()) return <Redirect to="/home" />;

  // First load gets skeletons; a refilter keeps the current numbers on screen
  // behind a plain spinner (never the logo — this is not an AI process).
  const initialLoading = loading && !data;
  const refiltering = loading && !!data;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header breadcrumbs={[{ name: t('Firm economics'), href: '/economics', current: true }]} />

      <div className="px-4 py-3 border-b border-hairline">
        <FirmEconomicsFilterBar value={filters} onChange={setFilters} />
      </div>

      <div className="flex-grow overflow-y-auto custom-scrollbar-main px-4 py-3 space-y-4">
        {renderMessage()}

        {!forbidden && !error && (initialLoading ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="kpi" />)}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="card" />)}
            </div>
          </>
        ) : (
          <>
            {refiltering && (
              <div className="flex items-center justify-end gap-2 text-xs text-fg-muted">
                <Spinner size="inline" ariaLabel={t('Updating')} />
                {t('Updating')}
              </div>
            )}

            <div className={refiltering ? 'space-y-4 opacity-60 transition-opacity' : 'space-y-4'}>
              {/* Totals */}
              <GlassCard padding="none">
                <div className="grid grid-cols-2 lg:grid-cols-5 divide-x divide-y lg:divide-y-0 divide-hairline-soft">
                  <KpiTile icon={<PiCoinsLight />} label={t('Revenue')} value={revenueValue} />
                  <KpiTile icon={<PiClockLight />} label={t('Total hours')} value={`${totalHours.toFixed(1)} ${t('h')}`} />
                  <KpiTile icon={<PiHourglassLight />} label={t('Billable hours')} value={`${billableHours.toFixed(1)} ${t('h')}`} />
                  <KpiTile icon={<PiGaugeLight />} label={t('Utilization')} value={utilization != null ? `${utilization}%` : '—'} />
                  <KpiTile
                    icon={<PiBriefcaseLight />}
                    label={t('Matters')}
                    value={String(totals.matter_count ?? widgets.matters.length)}
                  />
                </div>
              </GlassCard>

              {/* Revenue per currency — the detail the single Revenue tile cannot
                  carry once the firm bills in more than one. */}
              {byCurrency.length > 1 && (
                <GlassCard padding="none" title={t('Revenue by currency')}>
                  <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-hairline-soft">
                    {byCurrency.map((c) => (
                      <KpiTile
                        key={c.currency}
                        label={String(c.currency || '').toUpperCase()}
                        value={formatCurrency(c.revenue, c.currency)}
                      />
                    ))}
                  </div>
                </GlassCard>
              )}

              {/* Widgets */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <TopMattersWidget matters={widgets.matters} currency={currency} />
                <TopClientsWidget clients={widgets.clients} currency={currency} />
                <HoursByUserWidget users={widgets.users} currency={currency} onUserClick={onUserClick} />
                <RevenueTrendWidget trend={widgets.trend} currency={currency} />
              </div>
            </div>
          </>
        ))}
      </div>
    </div>
  );
};

export default FirmEconomicsDashboardPage;
